import React, { useCallback, useEffect, useState } from 'react';
import { Box, Collapse, Grid, IconButton, Typography } from '@mui/material';
import { AddCircleOutlineOutlined } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import TargetInstruction from './TargetInstruction';
import TargetPracticeInput from './TargetPracticeInput';
import InstanceDuration from './InstanceDuration';
import { capitalizeFirstLetter } from '../../../../../../helpers';
import { getThemeColor } from '../../../../../../utils/common';
import {
  FORTA_BLUE,
  FORTA_GRAY3,
  FORTA_GRAY4,
  FORTA_GRAY5,
  FORTA_WHITE,
} from '../../../../../../utils/colors';
import { TargetType, Themes } from '../../../../../../constants';
import {
  EventDurationMeasurementUnit,
  TargetStages,
  TPGoalDataCollectionMethod,
} from '../../../../../../components/layout/treatment-plan/goals/common/constants/general';
import TrashIcon from '../../../../../../components/svgs/TrashIcon';
import TargetPracticeInputReadOnly from './TargetPracticeInputReadOnly';
import TimeTagStartIcon from '../../../../../../assets/images/timetag-start.svg';
import TimeTagEndIcon from '../../../../../../assets/images/timetag-end.svg';
import { FHButton, FHTimeTag } from '../../../../../../components/base';
import { RateTutorialVideoLink } from '../../../constants';
import DurationTimeTracker from './DurationTimeTracker';
import RateTimeTracker from './RateTimeTracker';
import { shouldDisplayTargetStage } from '../../../utils';

import type { GoalTypes } from '../../../../../../components/layout/treatment-plan/goals/common/constants/general';
import type { Moment } from 'moment';
import type {
  ISessionPracticedTargetTrial,
  ITargetTrialDurationEvent,
  ITargetTrialInstanceDuration,
  ITargetTrialRateEvent,
} from '../../../../../../utils/types';

interface TargetPracticedProps {
  index: number;
  sessionId?: string;
  trial: ISessionPracticedTargetTrial;
  goalType: GoalTypes;
  dataCollectionMethod: TPGoalDataCollectionMethod | null;
  isEditable: boolean;
  savingFailed: boolean;
  savingInProgress: boolean;
  isCounterFocused: boolean;
  dateStart: Moment | undefined;
  expectedDuration: number | undefined;
  onCounterFocus: (isFocused: boolean) => void;
  onTrialDataChange: (trialDataDiff: any) => void;
  onDeleteRateEvent: (trialId: number, rateEventId: number) => void;
  onUpdateRateEvent: (trialId: number, rateEvent: ITargetTrialRateEvent) => void;
  onDelete: () => void;
  useNewUi: boolean;
  canDelete: boolean;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

const TargetPracticed: React.FC<TargetPracticedProps> = (props) => {
  const {
    index,
    trial,
    sessionId,
    goalType,
    dataCollectionMethod,
    isEditable,
    savingFailed,
    savingInProgress,
    isCounterFocused,
    dateStart,
    expectedDuration,
    onTrialDataChange,
    onDeleteRateEvent,
    onUpdateRateEvent,
    onDelete,
    onCounterFocus,
    useNewUi,
    canDelete,
    expanded,
    onExpandedChange,
  } = props;
  const { t } = useTranslation('sessionNotes');
  const [variant, setVariant] = useState<TargetType | null>(null);
  const [isDurationListExpanded, setIsDurationListExpanded] = useState(false); // only used for TargetType.InstancesWithDuration
  const [rateEvents, setRateEvents] = useState<ITargetTrialRateEvent[]>(trial?.rate_events ?? []);
  const [durationEvents, setDurationEvents] = useState<ITargetTrialDurationEvent[]>(
    trial?.duration_events ?? [],
  );

  useEffect(() => {
    if (trial?.target?.is_event_duration_required) {
      setVariant(TargetType.InstancesWithDuration);
      return;
    } else if (dataCollectionMethod === TPGoalDataCollectionMethod.PERCENTAGE) {
      setVariant(TargetType.SuccessFailure);
      return;
    } else if (dataCollectionMethod === TPGoalDataCollectionMethod.INSTANCES) {
      setVariant(TargetType.Instances);
      return;
    } else if (dataCollectionMethod === TPGoalDataCollectionMethod.RATE) {
      setVariant(TargetType.Rate);
      return;
    } else if (dataCollectionMethod === TPGoalDataCollectionMethod.DURATION) {
      setVariant(TargetType.Duration);
    }
  }, [trial?.target, dataCollectionMethod]);

  useEffect(() => {
    setRateEvents(trial?.rate_events ?? []);
  }, [trial?.rate_events]);

  useEffect(() => {
    setDurationEvents(trial?.duration_events ?? []);
  }, [trial?.duration_events]);

  const trialId = trial.id;
  let durationUnitLabel = '';
  switch (trial.target.event_duration_measurement_unit) {
    case EventDurationMeasurementUnit.Second:
      durationUnitLabel = t('goalsPracticed.second');
      break;
    case EventDurationMeasurementUnit.Minute:
      durationUnitLabel = t('goalsPracticed.minute');
      break;
    case EventDurationMeasurementUnit.Hour:
      durationUnitLabel = t('goalsPracticed.hour');
      break;
  }

  const handleAddRateEvent = useCallback(async () => {
    const newTime = moment();
    const startTime = moment(dateStart);
    const endTime = moment(dateStart).add(expectedDuration, 'seconds');

    let eventTime: Moment | null = newTime;
    let isOutsideRange = false;

    if (newTime.isSameOrBefore(startTime) || newTime.isSameOrAfter(endTime)) {
      eventTime = null;
      isOutsideRange = true;
    } else {
      eventTime = newTime.milliseconds(0);
    }

    const newRateEvent = {
      date_of_event: eventTime ? moment(eventTime).toDate() : undefined,
    };
    if (isOutsideRange) {
      setRateEvents([...rateEvents, newRateEvent]);
    }

    if (trialId === undefined) {
      return;
    }

    await onUpdateRateEvent(trialId, newRateEvent);
  }, [dateStart, expectedDuration, trial?.id, onUpdateRateEvent]);

  const toggleIsDurationListExpanded = () => {
    setIsDurationListExpanded(!isDurationListExpanded);
  };

  const handleUpdateRateEvent = (rateEvent: ITargetTrialRateEvent, newTime: Moment) => {
    if (trialId === undefined) {
      return;
    }

    setRateEvents((prev) =>
      prev.map((event) => {
        if (event.id === rateEvent.id) {
          return {
            ...rateEvent,
            date_of_event: moment(newTime).toDate(),
          };
        }
        return event;
      }),
    );
    onUpdateRateEvent(trialId, {
      ...rateEvent,
      date_of_event: moment(newTime).toDate(),
    });
  };

  const handleRateTutorialOpen = () => {
    window.open(RateTutorialVideoLink, '_blank');
  };

  const handleDeleteRateEvent = (rateEvent: ITargetTrialRateEvent) => {
    if (rateEvent.date_of_event === undefined || rateEvent.id === undefined) {
      setRateEvents(trial.rate_events ?? []);
      return;
    }

    if (trialId !== undefined) {
      onDeleteRateEvent(trialId, rateEvent.id);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: useNewUi ? { xs: 0, sm: '8px' } : '8px',
        mt: useNewUi ? { xs: 2, sm: 3 } : 3,
        padding: useNewUi ? { xs: 0, sm: '21px' } : { xs: '16px', sm: '21px' },
        width: '100%',
        textAlign: 'left',
        border: useNewUi
          ? { xs: 0, sm: `1px solid ${FORTA_GRAY3}` }
          : `1px solid ${FORTA_BLUE.light3}`,
        backgroundColor: useNewUi ? { xs: 'transparent', sm: FORTA_WHITE } : FORTA_BLUE.light3,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            textAlign: 'left',
            color: FORTA_BLUE.primary,
            fontWeight: 700,
          }}
        >
          {index}. {capitalizeFirstLetter(trial.target.label)}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isEditable && canDelete && (
            <IconButton
              data-testid={`deleteButtonTarget${capitalizeFirstLetter(
                trial.target.label.split(' ').join(''),
              )}`}
              sx={{
                mr: -1,
                backgroundColor: 'transparent',
                '&:disabled': {
                  opacity: 0.5,
                },
              }}
              disabled={!isEditable || savingInProgress || isCounterFocused}
              onClick={onDelete}
            >
              <TrashIcon color={FORTA_BLUE.primary} width={21} height={21} />
            </IconButton>
          )}
          {useNewUi && (
            <IconButton
              aria-label={expanded ? 'Collapse target' : 'Expand target'}
              onClick={() => onExpandedChange(!expanded)}
              sx={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <ExpandMoreIcon sx={{ color: FORTA_BLUE.primary }} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Collapse in={!useNewUi || expanded} timeout="auto">
        {shouldDisplayTargetStage(goalType, trial?.target?.stage) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'flex-start',
              gap: 2,
              mx: useNewUi ? { xs: 0, sm: 2 } : 2,
              mb: 1,
            }}
          >
            <Typography color={FORTA_BLUE.primary}>{t('goalsPracticed.currentStage')}:</Typography>
            <Typography>
              {trial?.target?.stage ? capitalizeFirstLetter(trial.target.stage) : ''}
              {trial?.target?.stage === TargetStages.Maintenance &&
                ` (${trial?.target?.maintenance_frequency})`}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            width: '100%',
          }}
        >
          {trial?.target?.instruction && (
            <TargetInstruction instructions={trial?.target?.instruction} />
          )}
          {(variant === TargetType.Instances || variant === TargetType.SuccessFailure) &&
            isEditable && (
              <TargetPracticeInput
                onTrialDataChange={onTrialDataChange}
                isEditable={isEditable}
                savingFailed={savingFailed}
                savingInProgress={savingInProgress}
                variant={variant}
                trial={trial}
                onCounterFocus={onCounterFocus}
              />
            )}
        </Box>
        {(variant === TargetType.Instances || variant === TargetType.SuccessFailure) &&
          !isEditable && (
            <Box>
              <TargetPracticeInputReadOnly
                onTrialDataChange={onTrialDataChange}
                isEditable={isEditable}
                savingFailed={savingFailed}
                savingInProgress={savingInProgress}
                variant={variant}
                trial={trial}
              />
            </Box>
          )}
        {variant === TargetType.InstancesWithDuration && (
          <Box
            sx={{
              mt: 1,
              ml: { sm: 2, xs: 0.2 },
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '8px',
              border: `1px solid ${getThemeColor(Themes.Primary).light2}`,
              padding: '0px 16px',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                height: '100%',
                py: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  pt: 1,
                  pb: { xs: 2, sm: 0 },
                }}
              >
                <Typography
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' },
                    alignItems: 'baseline',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    fontSize: '16px',
                    p: 1,
                    fontWeight: 'bold',
                    textAlign: 'left',
                    backgroundColor: FORTA_WHITE,
                  }}
                >
                  <span>{t('goalsPracticed.instanceTracker')}: &nbsp;</span>
                  <span style={{ fontWeight: 'normal' }}>
                    {trial?.instances?.length ?? 0} {t('goalsPracticed.instances')} -{' '}
                    {trial?.instances?.reduce(
                      (sum, a: ITargetTrialInstanceDuration) => sum + a.duration,
                      0,
                    ) ?? 0}{' '}
                    {durationUnitLabel}{' '}
                    {t('goalsPracticed.total')}
                  </span>
                </Typography>
                {(trial?.instances?.length ?? 0) > 0 && (
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      fontSize: '16px',
                      textAlign: 'center',
                      color: FORTA_BLUE.bright,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      mt: { xs: 0, sm: 2 },
                    }}
                    onClick={toggleIsDurationListExpanded}
                  >
                    {isDurationListExpanded
                      ? t('goalsPracticed.seeLess')
                      : t('goalsPracticed.seeMore')}
                  </Typography>
                )}
              </Box>
              {isEditable && (
                <TargetPracticeInput
                  onTrialDataChange={onTrialDataChange}
                  isEditable={isEditable}
                  savingFailed={savingFailed}
                  savingInProgress={savingInProgress}
                  variant={variant}
                  trial={trial}
                  target={trial?.target}
                  onCounterFocus={onCounterFocus}
                />
              )}
            </Box>

            <Collapse in={isDurationListExpanded} timeout="auto">
              <Box
                style={{
                  display: 'flex',
                  gap: '8px',
                  flexWrap: 'wrap',
                  padding: '16px 0px',
                }}
              >
                {trial?.instances?.map((instanceDuration: ITargetTrialInstanceDuration, index) => (
                  <InstanceDuration
                    testId={instanceDuration.duration}
                    isDeletable={isEditable}
                    key={instanceDuration?.id ?? 'key-' + index}
                    number={instanceDuration?.duration}
                    durationType={
                      trial?.target?.event_duration_measurement_unit
                        ? `${trial?.target.event_duration_measurement_unit.toLowerCase()}s`
                        : ''
                    }
                    onDelete={() => {
                      onTrialDataChange({
                        instances: trial?.instances?.filter((i) => i?.id !== instanceDuration?.id),
                        occurrence: trial?.instances?.length ? trial?.instances?.length - 1 : 0,
                      });
                    }}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        )}
        {variant === TargetType.Rate &&
          (useNewUi ? (
            <RateTimeTracker
              isEditable={isEditable}
              savingInProgress={savingInProgress}
              dateStart={dateStart}
              expectedDuration={expectedDuration}
              rateEvents={rateEvents}
              onAddRateEvent={handleAddRateEvent}
              onDeleteRateEvent={handleDeleteRateEvent}
              onUpdateRateEvent={handleUpdateRateEvent}
              onRateTutorialOpen={handleRateTutorialOpen}
            />
          ) : (
            <Box
              sx={{
                mt: 1,
                ml: { sm: 2, xs: 0.2 },
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '8px',
                backgroundColor: FORTA_WHITE,
                border: `1px solid ${getThemeColor(Themes.Primary).light2}`,
                padding: '0px 16px',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  height: '100%',
                  pb: 2,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    alignItems: 'flex-start',
                    pt: 1,
                    pb: { xs: 2, sm: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' },
                        alignItems: 'baseline',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        textAlign: 'left',
                      }}
                    >
                      <span>Rate tracker: &nbsp;</span>
                      <span style={{ fontWeight: 'normal' }}>
                        {rateEvents?.length ?? 0} total events
                      </span>
                    </Typography>
                    <IconButton onClick={handleRateTutorialOpen}>
                      <InfoOutlinedIcon sx={{ color: FORTA_BLUE.primary }} />
                    </IconButton>
                  </Box>
                  <Grid
                    xs={12}
                    sm={12}
                    sx={{
                      display: 'flex',
                      gap: 2,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 222,
                        minHeight: 60,
                        display: 'flex',
                        alignItems: 'center',
                        my: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          borderRadius: '4px',
                          py: 1,
                          px: 1,
                          gap: 3,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'not-allowed',
                          backgroundColor: FORTA_BLUE.light3,
                        }}
                      >
                        <img src={TimeTagStartIcon} alt="start-icon" width={21} height={17} />
                        <Typography color={FORTA_GRAY5} fontSize={16}>
                          {moment(dateStart).format('hh : mm : ss A')}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Dynamic time tags */}
                    {rateEvents?.map((rateEvent) => (
                      <Box
                        key={rateEvent.id || null}
                        sx={{
                          width: 222,
                          minHeight: 80,
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FHTimeTag
                          id={rateEvent.id || 0}
                          time={rateEvent.date_of_event ? moment(rateEvent.date_of_event) : null}
                          maxTime={
                            dateStart && expectedDuration
                              ? moment(dateStart).add(expectedDuration, 'seconds')
                              : moment()
                          }
                          minTime={moment(dateStart)}
                          debounceTime={700}
                          disabled={!isEditable || savingInProgress}
                          onDelete={() => handleDeleteRateEvent(rateEvent)}
                          onUpdate={(newTime) => {
                            handleUpdateRateEvent(rateEvent, newTime);
                          }}
                          containerStyles={{ width: '100%' }}
                        />
                      </Box>
                    ))}
                    {/* Static end time box */}
                    <Box
                      sx={{
                        width: 222,
                        minHeight: 80,
                        display: 'flex',
                        alignItems: 'center',
                        my: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          borderRadius: '4px',
                          py: 1,
                          px: 1,
                          gap: 3,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          cursor: 'not-allowed',
                          backgroundColor: FORTA_BLUE.light3,
                        }}
                      >
                        <Typography color={FORTA_GRAY5} fontSize={16}>
                          {dateStart && expectedDuration
                            ? moment(dateStart)
                                .add(expectedDuration, 'seconds')
                                .format('hh : mm : ss A')
                            : moment().format('hh : mm : ss A')}
                        </Typography>
                        <img src={TimeTagEndIcon} alt="end-icon" width={21} height={17} />
                      </Box>
                    </Box>
                  </Grid>
                  <Grid
                    xs={12}
                    sm={12}
                    sx={{
                      display: 'flex',
                      mt: 2,
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Typography sx={{ fontSize: '16px', color: FORTA_GRAY4 }}>
                      {t('goalsPracticed.toAddAnEvent')}
                    </Typography>
                    <FHButton
                      startIcon={<AddCircleOutlineOutlined />}
                      disabled={
                        rateEvents?.some((event) => !event.date_of_event) ||
                        !isEditable ||
                        savingInProgress
                      }
                      onClick={handleAddRateEvent}
                    >
                      {t('goalsPracticed.addEvent')}
                    </FHButton>
                  </Grid>
                </Box>
                {isEditable && (
                  <TargetPracticeInput
                    onTrialDataChange={onTrialDataChange}
                    isEditable={isEditable}
                    savingFailed={savingFailed}
                    savingInProgress={savingInProgress}
                    variant={variant}
                    trial={trial}
                    target={trial?.target}
                    onCounterFocus={onCounterFocus}
                  />
                )}
              </Box>
            </Box>
          ))}

        {variant === TargetType.Duration && trialId !== undefined && (
          <DurationTimeTracker
            trailId={trialId}
            sessionId={sessionId}
            isEditable={isEditable}
            durationEvents={durationEvents}
            useNewUi={useNewUi}
          />
        )}
      </Collapse>
    </Box>
  );
};

export default TargetPracticed;
